/**
 * G2PainIndex — dashboard widget surfacing G2 products with the most negative
 * reviews. Each is a paying-customer pain point = micro-SaaS launch target.
 *
 * Features:
 *  - Time-window filter pills: 7d / 30d / All-time
 *  - New products auto-included (live Mongo aggregation, no static list)
 *  - Expand any row → see 3 verbatim complaint snippets + source links
 *  - "Build the Alternative" button per product → Claude-drafted MVP brief
 *    (positioning, top 3 must-have features, pricing, GTM angle, ad headline)
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WINDOWS = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "all", label: "All-time" },
];

const G2PainIndex = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSlug, setExpandedSlug] = useState(null);
  const [windowKey, setWindowKey] = useState("all");

  // Brief modal state — keyed by product slug
  const [briefSlug, setBriefSlug] = useState(null);
  const [briefData, setBriefData] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState("");
  // Save-to-library state
  const [savedBriefIds, setSavedBriefIds] = useState({}); // {slug: brief_id}
  const [savingBrief, setSavingBrief] = useState(false);

  // Load already-saved briefs on mount so the button shows correct state.
  useEffect(() => {
    axios.get(`${API}/briefs`, { timeout: 8000 })
      .then((r) => {
        const map = {};
        (r.data?.briefs || []).forEach((b) => { map[b.product_slug] = b.id; });
        setSavedBriefIds(map);
      })
      .catch(() => {});
  }, []);

  // Recent discoveries — populated by the signal-driven G2 picker
  const [discoveries, setDiscoveries] = useState([]);
  useEffect(() => {
    axios.get(`${API}/g2/recent-discoveries?days=7&limit=5`, { timeout: 8000 })
      .then((r) => setDiscoveries(r.data?.discoveries || []))
      .catch(() => setDiscoveries([]));
  }, []);

  const fetchPainIndex = useCallback(() => {
    setLoading(true);
    let cancelled = false;
    axios
      .get(`${API}/g2/pain-index?limit=10&window=${windowKey}`, { timeout: 12000 })
      .then((r) => { if (!cancelled) setProducts(r.data?.products || []); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowKey]);

  useEffect(() => { fetchPainIndex(); }, [fetchPainIndex]);

  const openBrief = async (slug, product_name) => {
    setBriefSlug(slug);
    setBriefData({ product_name }); // show modal shell immediately
    setBriefLoading(true);
    setBriefError("");
    try {
      const r = await axios.post(`${API}/g2/${slug}/build-alternative`, {}, { timeout: 90000 });
      setBriefData(r.data);
    } catch (e) {
      setBriefError(e?.response?.data?.detail || e?.message || "Failed to draft brief.");
    } finally {
      setBriefLoading(false);
    }
  };
  const closeBrief = () => {
    setBriefSlug(null); setBriefData(null); setBriefError("");
  };

  const saveBriefToLibrary = async () => {
    if (!briefData?.brief || !briefSlug) return;
    setSavingBrief(true);
    try {
      const r = await axios.post(`${API}/briefs`, {
        product_slug: briefSlug,
        product_name: briefData.product_name,
        brief: briefData.brief,
        g2_url: briefData.g2_url,
        complaints_count: briefData.complaints_count,
      }, { timeout: 10000 });
      setSavedBriefIds((prev) => ({ ...prev, [briefSlug]: r.data?.id }));
    } catch (e) {
      // Inline alert; modal stays open so the user can retry
      alert(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSavingBrief(false);
    }
  };

  const isSaved = briefSlug && savedBriefIds[briefSlug];
  const downloadHref = (fmt) => isSaved ? `${API}/briefs/${savedBriefIds[briefSlug]}/export.${fmt}` : null;

  return (
    <>
      <section
        className="bg-white border border-zinc-200 rounded-2xl overflow-hidden"
        data-testid="g2-pain-index"
      >
        <header className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
              <span className="text-amber-500">⚠</span>
              G2 Pain Index
            </h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              Products with the most negative paying-customer reviews → ship a better alternative.
            </p>
          </div>
          <div className="flex items-center gap-1 bg-zinc-50 border border-zinc-200 rounded-full p-1" data-testid="g2-window-pills">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWindowKey(w.key)}
                className={`px-3 py-1 text-[11px] font-medium rounded-full transition-all ${windowKey === w.key ? 'bg-violet-600 text-white' : 'text-zinc-600 hover:text-zinc-900'}`}
                data-testid={`g2-window-${w.key}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="p-5 text-xs text-zinc-400 uppercase tracking-[0.2em] animate-pulse">
            Loading G2 pain index…
          </div>
        ) : !products.length ? (
          <div className="p-5 text-sm text-zinc-500">
            No negative reviews in this window yet. Try widening to All-time, or run a Premium Scan to harvest fresh G2 data.
          </div>
        ) : (
          <ol className="divide-y divide-zinc-100" data-testid="g2-pain-list">
            {products.map((p, idx) => {
              const isOpen = expandedSlug === p.product_slug;
              return (
                <li key={p.product_slug} data-testid={`g2-pain-row-${p.product_slug}`}>
                  <div className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-amber-50/40 transition-colors">
                    <button
                      type="button"
                      onClick={() => setExpandedSlug(isOpen ? null : p.product_slug)}
                      className="flex items-center gap-3 min-w-0 flex-1 text-left"
                      data-testid={`g2-pain-row-toggle-${p.product_slug}`}
                    >
                      <span className="font-mono-data text-xs text-zinc-400 w-5 text-right flex-shrink-0">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm font-medium text-zinc-900 truncate">
                        {p.product_name}
                      </span>
                      {p.is_new && (
                        <span
                          className="text-[9px] font-mono-data font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-emerald-500 text-white animate-pulse flex-shrink-0"
                          title="Just scraped for the first time in the last 7 days — newly discovered competitor"
                          data-testid={`g2-new-badge-${p.product_slug}`}
                        >
                          🆕 NEW
                        </span>
                      )}
                    </button>
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                      <span className="text-[11px] text-zinc-500 hidden md:inline">
                        avg ★{p.avg_rating ?? "?"}
                      </span>
                      <span className="text-[11px] font-mono-data px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                        {p.negative_count} neg / {p.total_reviews}
                      </span>
                      <span className="text-[11px] font-mono-data text-rose-700 w-11 text-right hidden sm:inline">
                        {p.negative_pct}%
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); openBrief(p.product_slug, p.product_name); }}
                        className="text-[10px] font-mono-data uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-violet-600 text-white hover:bg-violet-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                        title={`Draft an MVP brief for a ${p.product_name} alternative`}
                        data-testid={`g2-build-alt-btn-${p.product_slug}`}
                      >
                        <span>⚡</span> Build alt
                      </button>
                      <span className="text-zinc-400 text-xs">{isOpen ? "▾" : "▸"}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="px-5 pb-4 pt-1 space-y-2 bg-amber-50/30" data-testid={`g2-pain-samples-${p.product_slug}`}>
                      {p.samples.length === 0 && (
                        <div className="text-xs text-zinc-500">No sample complaints captured in this window.</div>
                      )}
                      {p.samples.map((s, i) => (
                        <blockquote
                          key={i}
                          className="text-xs text-zinc-700 border-l-2 border-rose-300 pl-3 py-1"
                        >
                          <span className="text-rose-600 font-mono-data text-[10px] mr-2">★{s.rating ?? "?"}</span>
                          "{s.snippet}"
                          {s.url && (
                            <a
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 text-violet-600 hover:underline text-[10px] uppercase tracking-wider"
                              data-testid={`g2-pain-sample-link-${p.product_slug}-${i}`}
                            >
                              source ↗
                            </a>
                          )}
                        </blockquote>
                      ))}
                      <a
                        href={p.g2_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-1 text-[10px] font-mono-data uppercase tracking-[0.15em] text-violet-700 hover:underline"
                        data-testid={`g2-pain-product-link-${p.product_slug}`}
                      >
                        See all {p.product_name} reviews on G2 ↗
                      </a>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* Most-discovered this week — products the signal-driven picker pulled in
          for the first time in the last 7 days, with the Reddit complaints that
          likely triggered the discovery. Hidden when the list is empty. */}
      {discoveries.length > 0 && (
        <section
          className="mt-6 rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-white border border-emerald-200/70 p-5 sm:p-6"
          data-testid="g2-recent-discoveries"
        >
          <header className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className="text-[15px] font-semibold text-zinc-900 flex items-center gap-2">
                <span className="text-emerald-500">🆕</span> Most-discovered this week
              </h3>
              <p className="text-[11.5px] text-zinc-500 mt-0.5">
                Brand-new competitors our picker pulled in based on Reddit complaint signal.
              </p>
            </div>
            <span className="text-[10px] font-mono-data uppercase tracking-[0.15em] text-emerald-700 bg-emerald-100/70 border border-emerald-300 px-2 py-0.5 rounded-full">
              {discoveries.length} new
            </span>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {discoveries.map((d) => (
              <div
                key={d.product_slug}
                className="rounded-xl bg-white border border-zinc-200 p-3.5 hover:border-emerald-300 transition-colors"
                data-testid={`g2-discovery-${d.product_slug}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-zinc-900 truncate">{d.product_name}</div>
                    <div className="text-[10.5px] font-mono-data text-zinc-500 mt-0.5">
                      {d.negative_count} neg / {d.total_reviews} reviews · avg ★{d.avg_rating ?? "?"}
                    </div>
                  </div>
                  <button
                    onClick={() => openBrief(d.product_slug, d.product_name)}
                    className="text-[9.5px] font-mono-data uppercase tracking-[0.15em] px-2 py-1 rounded-full bg-violet-600 text-white hover:bg-violet-700 transition-colors flex items-center gap-1 whitespace-nowrap flex-shrink-0"
                    title={`Draft an MVP brief for a ${d.product_name} alternative`}
                    data-testid={`g2-discovery-build-alt-${d.product_slug}`}
                  >
                    <span>⚡</span> Build alt
                  </button>
                </div>
                {(d.trigger_excerpts || []).length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    <div className="text-[9.5px] font-mono-data uppercase tracking-[0.15em] text-zinc-400">
                      Why we scraped this:
                    </div>
                    {d.trigger_excerpts.slice(0, 1).map((t, i) => (
                      <blockquote
                        key={i}
                        className="text-[11.5px] text-zinc-700 leading-snug italic border-l-2 border-emerald-300 pl-2"
                      >
                        "…{t.snippet}…"
                        {t.subreddit && (
                          <span className="not-italic ml-1.5 text-[10px] text-zinc-400">
                            · r/{t.subreddit}
                          </span>
                        )}
                      </blockquote>
                    ))}
                  </div>
                )}
                <a
                  href={d.g2_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2.5 text-[9.5px] font-mono-data uppercase tracking-[0.15em] text-violet-700 hover:underline"
                  data-testid={`g2-discovery-link-${d.product_slug}`}
                >
                  See {d.product_name} reviews on G2 ↗
                </a>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Build Alternative — AI-drafted MVP brief modal */}
      {briefSlug && (
        <div
          className="fixed inset-0 z-50 bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeBrief}
          data-testid="build-alt-modal"
        >
          <div
            className="bg-white max-w-2xl w-full rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-violet-600">⚡ MVP Brief — Drafted by Claude</div>
                <h3 className="text-lg font-semibold text-zinc-900 mt-0.5">
                  An alternative to {briefData?.product_name}
                </h3>
              </div>
              <button
                onClick={closeBrief}
                className="text-zinc-400 hover:text-zinc-900 text-xl px-2"
                data-testid="build-alt-close"
              >
                ✕
              </button>
            </header>

            <div className="p-6 space-y-5 text-sm" data-testid="build-alt-body">
              {briefLoading && (
                <div className="flex items-center gap-3 text-zinc-600">
                  <span className="inline-block w-3 h-3 rounded-full bg-violet-600 animate-pulse" />
                  Claude is reading the complaints and drafting your launch brief…
                  <span className="text-zinc-400 text-xs">(usually 15-30s)</span>
                </div>
              )}
              {briefError && (
                <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs">
                  {briefError}
                </div>
              )}
              {briefData?.brief && (
                <>
                  <div data-testid="brief-positioning">
                    <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">Positioning</div>
                    <div className="text-zinc-900 font-medium">{briefData.brief.positioning}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">Target persona</div>
                    <div className="text-zinc-800">{briefData.brief.target_persona}</div>
                  </div>
                  <div data-testid="brief-features">
                    <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-2">Top 3 must-have features</div>
                    <ol className="space-y-2">
                      {(briefData.brief.top_3_features || []).map((f, i) => (
                        <li key={i} className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <span className="font-mono-data text-[10px] text-violet-600 mt-0.5">{i + 1}</span>
                            <span className="text-zinc-900">{f}</span>
                          </div>
                          {briefData.brief.feature_evidence?.[i] && (
                            <blockquote className="mt-2 text-[11px] text-zinc-600 border-l-2 border-violet-300 pl-2 italic">
                              evidence: "{briefData.brief.feature_evidence[i]}"
                            </blockquote>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">Suggested pricing</div>
                      <div className="text-zinc-900 font-semibold">{briefData.brief.suggested_pricing}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">Complaints analyzed</div>
                      <div className="text-zinc-900 font-semibold">{briefData.complaints_count}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">GTM angle</div>
                    <div className="text-zinc-800 italic">"{briefData.brief.gtm_angle}"</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">First ad headline</div>
                    <div className="text-zinc-900 text-base font-semibold bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="brief-headline">
                      "{briefData.brief.first_ad_headline}"
                    </div>
                  </div>
                  {briefData.g2_url && (
                    <a
                      href={briefData.g2_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-[10px] font-mono-data uppercase tracking-[0.15em] text-violet-700 hover:underline pt-2"
                    >
                      Read the raw complaints on G2 ↗
                    </a>
                  )}
                </>
              )}
              {briefData?.parse_error && (
                <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                  Couldn't parse Claude's reply as JSON. Raw output:
                  <pre className="mt-2 whitespace-pre-wrap text-[11px] text-zinc-700">{briefData.raw}</pre>
                </div>
              )}
            </div>

            {briefData?.brief && (
              <footer className="px-6 py-3 border-t border-zinc-100 flex flex-wrap items-center justify-between gap-3 bg-zinc-50/60 sticky bottom-0" data-testid="brief-actions">
                <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500">
                  {isSaved ? "✓ Saved to your library" : "Save this brief for later"}
                </div>
                <div className="flex items-center gap-2">
                  {isSaved ? (
                    <>
                      <a
                        href={downloadHref("csv")}
                        download
                        className="px-3 py-1.5 text-[11px] font-mono-data uppercase tracking-[0.15em] rounded-full bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                        data-testid="brief-download-csv"
                      >
                        ⬇ CSV
                      </a>
                      <a
                        href={downloadHref("pdf")}
                        download
                        className="px-3 py-1.5 text-[11px] font-mono-data uppercase tracking-[0.15em] rounded-full bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                        data-testid="brief-download-pdf"
                      >
                        ⬇ PDF
                      </a>
                      <span className="px-3 py-1.5 text-[11px] font-mono-data uppercase tracking-[0.15em] rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
                        ★ Saved
                      </span>
                    </>
                  ) : (
                    <button
                      onClick={saveBriefToLibrary}
                      disabled={savingBrief}
                      className="px-4 py-1.5 text-[11px] font-mono-data uppercase tracking-[0.15em] rounded-full bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
                      data-testid="brief-save-btn"
                    >
                      {savingBrief ? "Saving…" : "★ Save to library"}
                    </button>
                  )}
                </div>
              </footer>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default G2PainIndex;
